import React from 'react';
import { FieldError, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { get } from 'lodash';
import {
  InputAdornment,
  TextFieldSpinner,
  TextFieldSuccessIndicator
} from 'edenred-ui';

import {
  getUnverifiedErrorName,
  getInvalidErrorName,
  getProcessingErrorName
} from './asyncValidatorErrorNames';

export function useAsyncValidatorController({
  value,
  error,
  name,
  invalidMsg,
  processingMsg,
  isStatusSuccess = isSuccess => isSuccess
}: {
  value: string;
  error?: FieldError;
  name: string;
  invalidMsg?: string;
  processingMsg?: string;
  isStatusSuccess?: (isSuccess: boolean) => boolean;
}) {
  const { t } = useTranslation();
  const {
    formState: { errors },
    trigger,
    setError,
    clearErrors
  } = useFormContext();

  const asyncInvalidError = get(errors, getInvalidErrorName(name));
  const isInvalidError = !!asyncInvalidError;
  const isProcessingError = !!get(errors, getProcessingErrorName(name));
  const asyncInvalidMessage =
    typeof asyncInvalidError?.message === 'string'
      ? asyncInvalidError.message
      : undefined;

  const isError = !!(error || isInvalidError);
  const isInfo = !isError && isProcessingError;

  const helperText =
    error?.message ||
    (isInvalidError &&
      (asyncInvalidMessage ? t(asyncInvalidMessage) : invalidMsg)) ||
    (isProcessingError && processingMsg);

  const getInputProps = () => {
    if (isProcessingError) {
      return {
        endAdornment: (
          <InputAdornment position="end">
            <TextFieldSpinner id={`${name}-loading`} />
          </InputAdornment>
        )
      };
    }

    if (
      isStatusSuccess(
        !isError && !!value && !get(errors, getUnverifiedErrorName(name))
      )
    ) {
      return {
        endAdornment: (
          <InputAdornment position="end">
            <TextFieldSuccessIndicator id={`${name}-success`} />
          </InputAdornment>
        )
      };
    }

    return undefined;
  };

  return {
    isError,
    isInfo,
    helperText,
    inputProps: getInputProps(),
    setAsyncUnverified() {
      setError(getUnverifiedErrorName(name), { message: '' });
    },
    clearAsyncUnverified() {
      const errorName = getUnverifiedErrorName(name);
      clearErrors(errorName);
      trigger(errorName);
    }
  };
}
